import React, { Dispatch, SetStateAction, createContext, useState, useContext, useRef } from 'react';
import { getParqueServicoByIdAnonimo, IParqueServicoAnonimo, verifyParqueExists } from 'api/services/ParqueService';
import { useQuery } from 'utils/query';
import { AreaColetaBuscada, getAreaColetaParquePorPosicao } from 'api/services/AreaColeta';

export interface IIframeState {
  latlong?: [number, number];
  tipoBusca?: 'plaqueta' | 'endereco' | string;
  dadosCadastroOcorrencia?: {
    ocorrencia?: {
      cod_parque_servico?: number;
    };
    ocorrenciasPontoServico?: [
      {
        cod_tipo_ocorrencia?: number;
        nom_endereco_livre?: string;
        cod_ponto_servico?: number;
        cod_bairro?: number;
        cod_municipio?: number;
        dsc_observacao?: string;
        json_ocorrencia_ponto_servico?: {
          coords?: {
            latitude: number;
            longitude: number;
          };

          endereco?: any;
          imagens?: { nom_caminho_imagem: string | null }[];
        };
      },
    ];
  };
}

export interface IDadosConsultaAreaColeta {
  loading?: boolean;
  loading_buscar_com_localizacao?: boolean;
  areaColetaBuscada?: AreaColetaBuscada;
  dataConsulta?: Date;
  enderecoBuscado: string;
}

export interface IIframeContext {
  parqueServico?: IParqueServicoAnonimo;
  coordsParque?: [number, number];
  codParqueServico: number;
  parqueIsValid: boolean;
  iframeState: IIframeState;
  setIframeState?: Dispatch<SetStateAction<IIframeState>>;
  dadosConsultaAreaColeta: IDadosConsultaAreaColeta;
  setDadosConsultaAreaColeta: Dispatch<SetStateAction<IDadosConsultaAreaColeta>>;
  handleBuscarAreaColeta: (usarPosicaoAtual?: boolean) => Promise<boolean>;
  erroConsultaAreaColeta: string | null;
}

interface IIframeProvider {
  children?: React.ReactNode;
}

export const IframeContext = createContext<IIframeContext>({} as IIframeContext);

export const IframeProvider = ({ children }: IIframeProvider) => {
  const query = useQuery();
  const [iframeState, setIframeState] = useState<IIframeState>({});
  const contadorBuscasAreaColeta = useRef(1);
  const [erroConsultaAreaColeta, setErroConsultaAreaColeta] = useState<string | null>(null);
  const [dadosConsultaAreaColeta, setDadosConsultaAreaColeta] = useState<IDadosConsultaAreaColeta>({
    enderecoBuscado: '',
  });
  const [parqueServico, setParqueServico] = useState<IParqueServicoAnonimo>();

  const codParqueQuery = Number(query.get('cod_parque_servico'));
  const codParqueStorage = Number(localStorage.getItem('cod_parque_servico'));

  const codParqueServico = codParqueQuery || codParqueStorage;

  const parqueIsValid = !!codParqueServico;

  const coordsParque = React.useMemo(() => {
    const centerCoordsParque = parqueServico?.area_json?.center;

    if (!centerCoordsParque) return undefined;

    // ta salvo trocado no banco :/
    const lat = centerCoordsParque?.lng;
    const lng = centerCoordsParque?.lat;

    return [lat, lng] as [number, number];
  }, [parqueServico?.area_json?.center]);

  const loadInfoParque = async () => {
    if (!parqueIsValid) return;

    const result = await getParqueServicoByIdAnonimo(codParqueServico);

    setParqueServico(result.resp);
  };

  const handleBuscarAreaColeta = async (usarPosicaoAtual?: boolean) => {
    try {
      const { enderecoBuscado: endereco } = dadosConsultaAreaColeta;
      let latitude: number | undefined = undefined;
      let longitude: number | undefined = undefined;

      if (usarPosicaoAtual) {
        const result = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            (err) => {
              console.error(err);
              reject(new Error('Não foi possível encontrar a sua localização.'));
            },
            { enableHighAccuracy: true },
          );
        });

        if (!result?.coords?.latitude || !result?.coords?.longitude || result?.coords?.accuracy > 150) {
          throw new Error('Não foi possível encontrar a sua localização.');
        }

        latitude = result.coords.latitude;
        longitude = result.coords.longitude;
      }

      if (!endereco?.trim?.() && !usarPosicaoAtual) {
        throw new Error(
          'Ops! O endereço não foi encontrado. Verifique se você digitou as informações corretas e completas. Tente novamente.',
        );
      }

      setDadosConsultaAreaColeta((prevState) => ({
        ...prevState,
        loading: !usarPosicaoAtual,
        loading_buscar_com_localizacao: !!usarPosicaoAtual,
      }));
      const result = await getAreaColetaParquePorPosicao({
        count: contadorBuscasAreaColeta.current,
        cod_parque_servico: codParqueServico,
        address: endereco,
        latitude,
        longitude,
      });
      setDadosConsultaAreaColeta((prevState) => ({
        ...prevState,
        loading: false,
        loading_buscar_com_localizacao: false,
      }));

      if (result.err || !result.resp) {
        contadorBuscasAreaColeta.current += 1;
      } else {
        contadorBuscasAreaColeta.current = 1;
      }

      if (result.err) throw new Error(result.err);

      const areaColeta = result.resp;

      if (!areaColeta) {
        throw new Error(
          'O endereço não está em nenhuma área de coleta, verifique se você digitou as informações corretas e completas. Tente novamente.',
        );
      }

      setDadosConsultaAreaColeta((prevState) => ({
        ...prevState,
        dataConsulta: new Date(),
        areaColetaBuscada: areaColeta,
      }));

      setErroConsultaAreaColeta(null);

      return true;
    } catch (error: any) {
      setErroConsultaAreaColeta(error?.message || 'Erro desconhecido');
      return false;
    }
  };

  React.useEffect(() => {
    loadInfoParque();
  }, [codParqueServico]);

  async function checkParqueIsValid() {
    if (!parqueIsValid) {
      console.error('Parque de serviço inválido');
      return;
    }

    const data = await verifyParqueExists(codParqueServico);

    if (!data?.resp?.check) {
      console.error('Parque de serviço inválido');
    }
  }

  React.useEffect(() => {
    if (codParqueQuery) {
      localStorage.setItem('cod_parque_servico', JSON.stringify(codParqueQuery));
    }
  }, []);

  React.useEffect(() => {
    checkParqueIsValid();
  }, [codParqueServico]);

  return (
    <IframeContext.Provider
      value={{
        erroConsultaAreaColeta,
        handleBuscarAreaColeta,
        parqueServico,
        coordsParque,
        codParqueServico,
        parqueIsValid,
        iframeState,
        setIframeState,
        dadosConsultaAreaColeta,
        setDadosConsultaAreaColeta,
      }}
    >
      {children}
    </IframeContext.Provider>
  );
};

export const useIframeContext = () => useContext(IframeContext);

export default IframeContext;
